// Зеркало пользовательских настроек для офлайн-экранов (PWA офлайн, дизайн-
// паритет). users.card_view и users.menu_visibility живут на сервере — vanilla
// офлайн-страницы (offline-catalog.html / offline-item.html) их не видят.
// Приложение при онлайне зеркалит их в localStorage одним JSON-объектом,
// офлайн-страницы читают и повторяют поведение онлайна: вид карточек,
// состав нижнего бара, имя в шапке.
//
// Pure-модуль: parse/merge тестируются в vitest без DOM. Запись в localStorage
// делает тонкая обёртка web/lib/offlinePrefsMirror.ts.

import { parseCardView, type CardView } from './cardView';
import {
  parseMenuVisibility,
  type MenuVisibility,
} from './menuVisibility';

/** Ключ localStorage. Тот же origin, что и приложение, — офлайн-страницы
 *  читают напрямую (vanilla-зеркало парсера ниже). */
export const OFFLINE_PREFS_STORAGE_KEY = 'favbox-offline-prefs';

export type OfflinePrefs = {
  cardView: CardView;
  menu: MenuVisibility;
  /** Имя юзера для шапки (у входа в настройки, как в Navbar). null — не знаем. */
  firstName: string | null;
};

/**
 * Нормализует сырое содержимое localStorage (строка JSON / null / мусор)
 * в полный OfflinePrefs. Любая порча → дефолты (compact + всё скрыто),
 * т.е. ровно то, что видит новый юзер онлайн.
 */
export function parseOfflinePrefs(raw: unknown): OfflinePrefs {
  let src: Record<string, unknown> = {};
  if (typeof raw === 'string' && raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') src = parsed as Record<string, unknown>;
    } catch {
      // мусор в localStorage — дефолты
    }
  } else if (raw && typeof raw === 'object') {
    src = raw as Record<string, unknown>;
  }
  return {
    cardView: parseCardView(src.cardView),
    menu: parseMenuVisibility(src.menu),
    firstName: typeof src.firstName === 'string' && src.firstName ? src.firstName : null,
  };
}

/**
 * Слить частичное обновление в существующее сырое значение и вернуть строку
 * для записи в localStorage. Не затирает поля, которых нет в partial, —
 * зеркала пишутся из трёх разных мест (Navbar / MenuProvider / DashboardShell).
 */
export function mergeOfflinePrefs(
  existingRaw: unknown,
  partial: Partial<OfflinePrefs>,
): string {
  const current = parseOfflinePrefs(existingRaw);
  return JSON.stringify({ ...current, ...partial });
}
